/* verqo — Landing V2 Preview (rev 3)
   White-bg marketing site. Original brand lockup restored.
   Hero reframed around the wholesale-OS narrative (six players · one ledger).
   Restored Problem + Solution sections covering BOTH seller and buyer pain.
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, animate, useMotionValue } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Banknote, Sparkles, ShieldCheck, Truck,
  Repeat, FileText, Brain, Wallet, TrendingUp, CircleDot, Building2,
  Zap, Clock, BadgePercent, Star, Layers, AlertCircle, Network,
  ScrollText, Database, Workflow, Lock, Shirt, Home,
  Smartphone, UtensilsCrossed, Heart, ChevronDown, Gem, FileCheck,
  PawPrint, ShoppingBasket,
  Boxes, Globe, MousePointerClick, FileSignature, LayoutDashboard,
  Plane, Bot, RotateCw, Users, CreditCard,
  MessageSquare, Receipt, Plug, Calendar,
} from "lucide-react";
import { InlinePortalTabs, SELLER_SCENES } from "./PortalJourney";
import { VerqoLockup, CheckVGlyph } from "../../components/VerqoLockup";
import { BuyerCreditGraph } from "./BuyerHeroV2Preview";
import MarketingMobileNav from "../../components/MarketingMobileNav";
import { HERO_VARIANTS } from "./HeroVariants";

/* ────────────────────────────────────────────────────────────────────── */
/* Brand atoms                                                            */
/* ────────────────────────────────────────────────────────────────────── */
const BRAND = { fg: "#0c1612", accent: "#dcff3a", accentFg: "#0c1612" };

const Eyebrow = ({ children, testid }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700" data-testid={testid}>
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

function CountUp({ to, prefix = "", suffix = "", decimals = 0 }) {
  const value = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    const controls = animate(value, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toLocaleString("en-US", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })),
    });
    return controls.stop;
  }, [to]);
  return <span>{prefix}{display}{suffix}</span>;
}

/* ────────────────────────────────────────────────────────────────────── */
/* Customer wordmark wall                                                 */
/* ────────────────────────────────────────────────────────────────────── */
const CUSTOMERS = [
  { name: "Lord & Taylor",   font: "'Cormorant Garamond', Georgia, serif",   weight: 500, italic: true,  spacing: "0.01em" },
  { name: "Ashley Stewart",  font: "Georgia, 'Times New Roman', serif",      weight: 600, italic: false, spacing: "0.02em" },
  { name: "SHEIN",           font: "Inter, ui-sans-serif, sans-serif",        weight: 800, italic: false, spacing: "0.12em" },
  { name: "stores.com",      font: "Inter, ui-sans-serif, sans-serif",        weight: 700, italic: false, spacing: "-0.02em" },
  { name: "whatnot",         font: "Inter, ui-sans-serif, sans-serif",        weight: 800, italic: false, spacing: "-0.04em" },
];

function CustomerWordmark({ c }) {
  return (
    <div
      className="text-slate-700 hover:text-slate-900 transition-colors select-none whitespace-nowrap"
      style={{
        fontFamily: c.font, fontWeight: c.weight,
        fontStyle: c.italic ? "italic" : "normal",
        letterSpacing: c.spacing,
        fontSize: "clamp(20px, 2.2vw, 28px)",
      }}
      data-testid={`logo-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {c.name}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Flow diagram (5 steps)                                                 */
/* ────────────────────────────────────────────────────────────────────── */
const FLOW_STEPS = [
  { icon: FileText,  title: "Buyer places order",  body: "PO arrives via PDF · Shopify · API. verqo AI parses it in <1 second.",        emphasis: false },
  { icon: Brain,     title: "AI approves credit",   body: "Buyer Score · KYB · OFAC · AML evaluated in seconds. No paper forms.",        emphasis: false },
  { icon: Banknote,  title: "Seller paid same-day", body: "Day-0 ACH or wire into your wallet. Re-buy inventory this week.",             emphasis: true  },
  { icon: Truck,     title: "Goods ship",            body: "Carrier integrated. Tracking flows back into your ledger automatically.",     emphasis: false },
  { icon: Repeat,    title: "Buyer repays · Net-60", body: "Auto-debit on due date. Insurance-backed safety net if they don't.",          emphasis: false },
];

function FlowDiagram() {
  return (
    <div className="relative" data-testid="flow-diagram">
      <div className="hidden md:block absolute top-[36px] left-[8%] right-[8%] h-[2px] bg-gradient-to-r from-lime-200 via-lime-400 to-lime-200" />
      <motion.div
        className="hidden md:block absolute top-[31px] w-3 h-3 rounded-full bg-lime-500 shadow-[0_0_18px_rgba(132,204,22,0.8)]"
        initial={{ left: "8%" }}
        animate={{ left: ["8%", "92%"] }}
        transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
        data-testid="flow-particle"
      />
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-3 relative">
        {FLOW_STEPS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.07}>
            <div className="flex flex-col items-center text-center px-2">
              <div className={`w-[72px] h-[72px] rounded-full grid place-items-center mb-5 border-2 ${
                s.emphasis
                  ? "bg-lime-400 border-lime-500 shadow-[0_8px_30px_-4px_rgba(132,204,22,0.5)]"
                  : "bg-white border-slate-200 shadow-sm"
              }`}>
                <s.icon className={`w-6 h-6 ${s.emphasis ? "text-slate-900" : "text-slate-700"}`} strokeWidth={1.8} />
              </div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400 mb-1.5">Step {i + 1}</div>
              <div className={`text-[15px] font-semibold ${s.emphasis ? "text-lime-700" : "text-slate-900"}`}>{s.title}</div>
              <div className="mt-2 text-[12.5px] leading-relaxed text-slate-500">{s.body}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Live portal mockups                                                    */
/* ────────────────────────────────────────────────────────────────────── */
function BrowserFrame({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-[0_24px_64px_-24px_rgba(15,23,42,0.18)] overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
        </div>
        <div className="ml-2 text-[10.5px] text-slate-400 tracking-wide truncate">{title}</div>
      </div>
      <div className="p-5 md:p-6 bg-white">{children}</div>
    </div>
  );
}

function SellerPortalMock() {
  const seedInvoices = [
    { id: "INV-3382", buyer: "Lord & Taylor",   amt: 18420 },
    { id: "INV-3381", buyer: "Ashley Stewart",  amt:  6240 },
    { id: "INV-3380", buyer: "SHEIN wholesale", amt: 42100 },
    { id: "INV-3379", buyer: "Whatnot",         amt:  9870 },
  ];
  const ROTATING_BUYERS = ["Lord & Taylor", "Ashley Stewart", "SHEIN wholesale", "stores.com", "Whatnot", "Glowco Distributing", "Acme Packaging", "Pacific Sun Apparel", "Bayside Beauty Co.", "RetailMax"];
  const [invoices, setInvoices] = useState(seedInvoices);
  const [balance, setBalance] = useState(128420);
  const [received, setReceived] = useState(34820);
  const [count, setCount] = useState(12);
  const [nextId, setNextId] = useState(3383);

  useEffect(() => {
    const interval = setInterval(() => {
      const buyer = ROTATING_BUYERS[Math.floor(Math.random() * ROTATING_BUYERS.length)];
      const amt = Math.floor(Math.random() * 18000) + 1500;
      const newInv = { id: `INV-${nextId}`, buyer, amt };
      setInvoices((prev) => [newInv, ...prev.slice(0, 3)]);
      setBalance((b) => b + amt);
      setReceived((r) => r + amt);
      setCount((c) => c + 1);
      setNextId((id) => id + 1);
    }, 6000);
    return () => clearInterval(interval);
  }, [nextId]);

  return (
    <BrowserFrame title="seller.verqo.com / wallet" testid="seller-portal-mock">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Wallet · today</div>
        <div className="text-[10.5px] text-emerald-600 inline-flex items-center gap-1 font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
        </div>
      </div>
      <motion.div
        key={balance}
        initial={{ scale: 1.03, color: "#84cc16" }}
        animate={{ scale: 1, color: "#0f172a" }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-semibold tracking-tight tabular-nums"
      >
        ${balance.toLocaleString()}
      </motion.div>
      <div className="text-[11.5px] text-slate-500 mt-1">+ ${received.toLocaleString()} received today · {count} invoices</div>
      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-3">Recent disbursements</div>
        <div className="space-y-2">
          {invoices.map((iv) => (
            <motion.div
              key={iv.id}
              initial={{ opacity: 0, x: -12, height: 0 }}
              animate={{ opacity: 1, x: 0, height: "auto" }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center justify-between text-[12.5px]"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-lime-500" />
                <span className="text-slate-700 font-medium">{iv.buyer}</span>
                <span className="text-slate-400 text-[10.5px]">· {iv.id}</span>
              </div>
              <div className="text-slate-900 font-semibold tabular-nums">+${iv.amt.toLocaleString()}</div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Cash freed up this month</div>
        <div className="text-[13px] font-semibold text-lime-700 tabular-nums">${(482300 + (balance - 128420)).toLocaleString()}</div>
      </div>
    </BrowserFrame>
  );
}

function BuyerPortalMock() {
  const limit = 750000;
  const [used, setUsed] = useState(482000);
  const [orders, setOrders] = useState([
    { s: "Fabric & Trim Co.",   amt: 24500, due: 47 },
    { s: "Acme Packaging",      amt: 12300, due: 32 },
    { s: "Glowco Distributing", amt:  8800, due: 12 },
  ]);
  const ROTATING_SUPPLIERS = ["Fabric & Trim Co.", "Acme Packaging", "Glowco Distributing", "Pacific Sun Apparel", "Bayside Beauty Co.", "Northstar Goods", "Sierra Supply"];

  useEffect(() => {
    const interval = setInterval(() => {
      const supplier = ROTATING_SUPPLIERS[Math.floor(Math.random() * ROTATING_SUPPLIERS.length)];
      const amt = Math.floor(Math.random() * 15000) + 3000;
      const due = Math.floor(Math.random() * 50) + 14;
      setOrders((prev) => [{ s: supplier, amt, due }, ...prev.slice(0, 2)]);
      setUsed((u) => Math.min(u + amt, limit - 5000));
    }, 7500);
    return () => clearInterval(interval);
  }, []);
  const pct = Math.round((used / limit) * 100);

  return (
    <BrowserFrame title="buyer.verqo.com / credit" testid="buyer-portal-mock">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Credit line</div>
        <div className="text-[10.5px] text-slate-500">Net-60 · insurance-backed</div>
      </div>
      <div className="flex items-baseline gap-2">
        <motion.div
          key={used}
          initial={{ scale: 1.03 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold tracking-tight text-slate-900 tabular-nums"
        >
          ${used.toLocaleString()}
        </motion.div>
        <div className="text-[12.5px] text-slate-400 tabular-nums">/ ${limit.toLocaleString()}</div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-lime-400 to-lime-600 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <div className="text-[11px] text-slate-500 mt-1.5">{pct}% utilized · ${(limit - used).toLocaleString()} available</div>
      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-3">Active orders</div>
        <div className="space-y-2">
          {orders.map((o) => (
            <motion.div
              key={`${o.s}-${o.amt}`}
              initial={{ opacity: 0, y: 6, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center justify-between text-[12.5px]"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-slate-700 font-medium">{o.s}</span>
              </div>
              <div className="text-right">
                <div className="text-slate-900 font-semibold tabular-nums">${o.amt.toLocaleString()}</div>
                <div className="text-[10.5px] text-slate-400">in {o.due} days</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Network graph — six players around the verqo ledger hub                */
/* ────────────────────────────────────────────────────────────────────── */
export function NetworkGraph() {
  const players = [
    { id: "buyer",   label: "Buyer",    icon: Building2,    angle: -90  },
    { id: "seller",  label: "Seller",   icon: Wallet,       angle: -30  },
    { id: "lender",  label: "Lender",   icon: Banknote,     angle:  30  },
    { id: "carrier", label: "Carrier",  icon: Truck,        angle:  90  },
    { id: "insurer", label: "Insurer",  icon: ShieldCheck,  angle:  150 },
    { id: "bank",    label: "Bank",     icon: Database,     angle:  210 },
  ];
  const cx = 200, cy = 200, r = 135;
  const polar = (a) => ({ x: cx + r * Math.cos(a * Math.PI / 180), y: cy + r * Math.sin(a * Math.PI / 180) });

  return (
    <div className="relative w-full max-w-[440px] aspect-square mx-auto" data-testid="network-graph">
      <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
        <defs>
          <radialGradient id="hubGlow">
            <stop offset="0%"  stopColor="#dcff3a" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#dcff3a" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#dcff3a" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Pulsing halo around hub */}
        <motion.circle
          cx={cx} cy={cy}
          fill="url(#hubGlow)"
          initial={{ r: 50 }}
          animate={{ r: [50, 90, 50] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Connection lines */}
        {players.map((p) => {
          const { x, y } = polar(p.angle);
          return <line key={`line-${p.id}`} x1={x} y1={y} x2={cx} y2={cy} stroke="rgba(220,255,58,0.25)" strokeWidth="1.2" />;
        })}

        {/* Particles flowing each direction along every line */}
        {players.map((p, i) => {
          const { x, y } = polar(p.angle);
          return (
            <g key={`particles-${p.id}`}>
              <motion.circle
                r="3.5"
                fill="#dcff3a"
                initial={{ cx: x, cy: y }}
                animate={{ cx: [x, cx, x], cy: [y, cy, y] }}
                transition={{ duration: 4, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
              />
              <motion.circle
                r="2.5"
                fill="#ecfccb"
                initial={{ cx: cx, cy: cy }}
                animate={{ cx: [cx, x, cx], cy: [cy, y, cy] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.8 + i * 0.4, ease: "easeInOut" }}
              />
            </g>
          );
        })}

        {/* Center hub — Check-V Hybrid glyph (matches brand lockup) */}
        <circle cx={cx} cy={cy} r="38" fill="#dcff3a" />
        <path
          d={`M ${cx - 22} ${cy - 7} L ${cx - 4} ${cy + 20} L ${cx + 22} ${cy - 22}`}
          stroke="#0c1612"
          strokeWidth="7.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* "the ledger" label inside SVG so it scales with diagram and never overlaps the hub */}
        <text
          x={cx}
          y={cy + 64}
          textAnchor="middle"
          fontSize="11"
          fontWeight="500"
          letterSpacing="2.4"
          fill="rgba(255,255,255,0.6)"
          fontFamily="Inter, system-ui, sans-serif"
        >
          THE LEDGER
        </text>
      </svg>

      {/* HTML labels positioned over SVG for the six players */}

      {players.map((p) => {
        const { x, y } = polar(p.angle);
        const xPct = (x / 400) * 100;
        const yPct = (y / 400) * 100;
        return (
          <div
            key={p.id}
            className="absolute flex flex-col items-center"
            style={{ left: `${xPct}%`, top: `${yPct}%`, transform: "translate(-50%, -50%)" }}
            data-testid={`network-node-${p.id}`}
          >
            <div className="w-12 h-12 rounded-full bg-white border-2 border-lime-400 grid place-items-center shadow-[0_0_20px_-4px_rgba(220,255,58,0.6)]">
              <p.icon className="w-5 h-5 text-slate-900" strokeWidth={1.8} />
            </div>
            <div className="mt-2 text-[10.5px] font-semibold uppercase tracking-wider text-white whitespace-nowrap">{p.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Industries served · comparison rows · FAQ · trust badges               */
/* ────────────────────────────────────────────────────────────────────── */
export const INDUSTRIES = [
  { icon: Sparkles,         name: "Beauty" },
  { icon: Shirt,            name: "Fashion & Apparel" },
  { icon: UtensilsCrossed,  name: "Food & Beverage" },
  { icon: PawPrint,         name: "Pet" },
  { icon: Heart,            name: "Health & Wellness" },
  { icon: ShoppingBasket,   name: "Household CPG" },
];

const COMPARE_ROWS = [
  { criterion: "Time to cash",          verqo: "Same-day · day-0 ACH",                       factoring: "3–7 days · paperwork-heavy",          net60: "60+ days",                 diy: "Whenever the buyer pays" },
  { criterion: "Buyer relationship",    verqo: "Untouched · invisible to buyer",             factoring: "Disrupted · factor calls your buyer",  net60: "Friendly but slow",        diy: "Strained when chasing" },
  { criterion: "Onboarding speed",      verqo: "Under 2 hours · self-serve",                 factoring: "2–6 weeks",                            net60: "Per-buyer paper apps",     diy: "Instant — but you're the bank" },
  { criterion: "Pricing transparency",  verqo: "Flat monthly subscription · no GMV cut",     factoring: "Tiered fees · reserves · hidden costs", net60: "$0 fee · 100% risk",       diy: "$0 fee · 100% risk" },
  { criterion: "Default protection",    verqo: "Insurance baked in (Allianz)",               factoring: "Recourse — you eat the loss",          net60: "None",                     diy: "None" },
  { criterion: "Reusable credit",       verqo: "Buyers carry one line across every seller",  factoring: "Per-invoice only",                     net60: "Per-supplier",             diy: "Per-supplier" },
  { criterion: "Compliance & audit",    verqo: "KYB · OFAC · AML · audit chain built-in",    factoring: "Manual",                               net60: "None",                     diy: "None" },
];

const FAQS = [
  { q: "How fast do I actually get paid?", a: "Same business day. The moment your buyer's order is approved, verqo wires the invoice amount (minus our flat fee) directly to your wallet via ACH or wire — no waiting on Net-30/60/90 terms." },
  { q: "Does my buyer know verqo is involved?", a: "Only if you want them to. By default, verqo runs invisibly — your buyer still sees your invoice, your branding, and pays you under their existing terms. We just front the cash." },
  { q: "What happens if a buyer doesn't pay?", a: "verqo absorbs the loss. Every funded invoice is covered by trade-credit insurance (underwritten by Allianz). At day 60 past due, insurance takes over — you keep the cash you were paid." },
  { q: "How is this different from factoring?", a: "Factoring takes 3–7 days, charges hidden reserve fees, and disrupts your buyer relationship because the factor contacts them directly. verqo is same-day, flat-fee, and invisible to the buyer." },
  { q: "Who underwrites the credit decisions?", a: "verqo's AI Buyer Score combines KYB · OFAC · AML · payment history · public-records signals — calibrated against our internal lender pool. Decisions take under 60 seconds." },
  { q: "Can my buyers re-use their verqo credit line with other suppliers?", a: "Yes — that's the magic. Each buyer gets one portable credit line that works across every supplier on verqo, which means buyers prefer ordering from sellers on the network." },
  { q: "What does it cost?", a: "Sellers pay a flat monthly subscription (Starter $99/mo · Growth $599/mo · Pro $1,499/mo · Enterprise $3,999/mo) plus a per-active-buyer fee above each plan's included count. Buyers pay $0 — always. No setup fees, no minimums, no GMV cut, no surprise charges." },
  { q: "How long does onboarding take?", a: "Under 2 hours. You upload basic KYB documents (EIN, articles of incorporation, bank info) and you're ready to fund invoices the same day." },
  { q: "Is verqo a bank?", a: "No — verqo is a fintech that partners with a chartered sponsor bank for money movement, plus regulated insurance carriers for trade-credit cover. Your funds are FDIC-insured at the sponsor bank." },
];

export const TRUST_BADGES = [
  { icon: ShieldCheck,  label: "SOC 2 Type II",   sub: "audited & certified" },
  { icon: Lock,         label: "256-bit TLS",     sub: "in-transit & at-rest" },
  { icon: FileCheck,    label: "OFAC / AML",      sub: "screened on every party" },
  { icon: ScrollText,   label: "Reg B / ECOA",    sub: "adverse-action compliant" },
  { icon: BadgePercent, label: "ESIGN Act",       sub: "e-signature lawful" },
  { icon: Database,     label: "FDIC sponsor",    sub: "via partner bank" },
];

/* ────────────────────────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────── */
/* "Only on verqo" bento — seller + buyer moats                           */
/* ────────────────────────────────────────────────────────────────────── */
export function MoatTile({ icon: Icon, n, title, body, variant = "default", testid }) {
  const base = "relative rounded-2xl border p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 group";
  const styles = {
    hero:    "bg-[#dcff3a] border-lime-400/50 text-slate-900 lg:col-span-2 lg:row-span-2 shadow-[0_20px_60px_-30px_rgba(34,80,8,0.45)]",
    wide:    "bg-white border-slate-200/80 lg:col-span-2 hover:shadow-[0_24px_60px_-32px_rgba(15,23,42,0.18)] hover:border-slate-300",
    default: "bg-white border-slate-200/80 hover:shadow-[0_24px_60px_-32px_rgba(15,23,42,0.18)] hover:border-slate-300",
  };
  const iconWrap = variant === "hero"
    ? "w-12 h-12 rounded-xl bg-slate-900 text-lime-300 grid place-items-center shadow-md"
    : "w-11 h-11 rounded-xl bg-slate-900/[0.04] text-slate-700 grid place-items-center group-hover:bg-lime-100 group-hover:text-lime-800 transition-colors";

  return (
    <div className={`${base} ${styles[variant]}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-5">
        <div className={iconWrap}>
          <Icon className={variant === "hero" ? "w-6 h-6" : "w-5 h-5"} strokeWidth={1.6} />
        </div>
        <span className={`mono text-[10px] tracking-[0.2em] ${variant === "hero" ? "text-slate-900/50" : "text-slate-400"}`}>
          {String(n).padStart(2, "0")}
        </span>
      </div>
      <h3 className={`font-semibold tracking-tight ${variant === "hero" ? "text-[28px] md:text-[32px] leading-[1.05]" : "text-[19px] md:text-[20px] leading-[1.15]"}`}>
        {title}
      </h3>
      <p className={`mt-3 ${variant === "hero" ? "text-[15px] md:text-[16px] text-slate-900/75 leading-relaxed" : "text-[13.5px] text-slate-600 leading-relaxed"}`}>
        {body}
      </p>
    </div>
  );
}

const SELLER_MOAT_TILES = [
  { icon: Banknote,        title: "Same-day cash on every invoice",  body: "The moment your buyer's PO is approved, we wire the invoice amount straight to your wallet. Net-30/60/90 disappears.", variant: "hero" },
  { icon: ShieldCheck,     title: "Zero default risk — Allianz baked in", body: "Every funded invoice is insured by Allianz Trade. The cover lives inside the platform — no separate policy to manage.", variant: "wide" },
  { icon: Repeat,          title: "Never chase a payment again", body: "If a buyer goes 60 days past due, Allianz takes over. You keep the cash. No collections team. No write-offs.", variant: "default" },
  { icon: Wallet,          title: "D+1 wallet → bank", body: "Your wallet sweeps to your bank automatically on the schedule you set. Or hit withdraw anytime.", variant: "default" },
  { icon: Users,           title: "Buyer 360° drawer", body: "Score · Allianz cover · payment history · open orders · open invoices — every buyer, one panel.", variant: "default" },
  { icon: ScrollText,      title: "PO inbox parser", body: "Drop a PDF, forward an email, paste a CSV, or hit the API. We extract every line, match SKUs, draft the order.", variant: "default" },
  { icon: Boxes,           title: "Dropship Automation", body: "One toggle connects a buyer's Shopify store. Orders auto-fulfill daily, tracking auto-pushes, invoices auto-issue.", variant: "default" },
  { icon: Globe,           title: "Your portal, your brand", body: "Custom domain (shop.yourbrand.com), your logo, your colors. Buyers feel like they're in your store — not ours.", variant: "default" },
];

export const BUYER_MOAT_TILES = [
  { icon: Network,           title: "One credit line. Every supplier.", body: "Apply once. Use it across every brand on verqo. No re-applying. No paper trade references. No fax-back forms.", variant: "hero" },
  { icon: CreditCard,        title: "Net-30/60/90 on every invoice", body: "Buy now, pay later — at every supplier on the network, on the terms each seller offers you.", variant: "wide" },
  { icon: Sparkles,          title: "Approved in under 60 seconds", body: "Plaid your bank, KYB your EIN. Our AI underwriter drafts your memo and unlocks your line in real time.", variant: "default" },
  { icon: MousePointerClick, title: "One click. Pay & get your goods.", body: "No bank wires. No ACH delays. Pay with your credit line — your supplier ships the moment you confirm.", variant: "default" },
  { icon: FileSignature,     title: "Buyer PO Generator", body: "Build a PO in 30 seconds — line items, payment terms, ship-to. Send to any supplier on or off verqo.", variant: "default" },
  { icon: Truck,             title: "Book shipping inside the portal", body: "UPS · FedEx · USPS · LTL freight. Rates, labels, live tracking — all in your verqo ledger.", variant: "default" },
  { icon: LayoutDashboard,   title: "One Payments tab for everything", body: "Wallet · Credit · Methods · Activity — one screen for your money, your terms, and your history.", variant: "default" },
  { icon: Boxes,             title: "Dropship Automation", body: "Sell on Shopify? Connect your store and your suppliers auto-fulfill daily. You never touch a label.", variant: "default" },
];

const SELLER_ROADMAP = [
  { title: "Cross-border with trust",            desc: "Multi-currency, FX-locked, insurance that travels across borders." },
  { title: "Inventory & Warehousing",            desc: "Stock levels, reorder alerts, multi-warehouse — built into the ledger." },
  { title: "Advanced Reports & Analytics",       desc: "Margin, velocity, COGS, buyer-cohort dashboards." },
  { title: "Developer API & sandbox",            desc: "API keys, idempotency, sandbox mode, OpenAPI docs." },
  { title: "Discover buyers on the network",     desc: "Browse and connect with already-credentialed wholesale buyers." },
  { title: "Multi-tenant marketplace publishing", desc: "Run multiple brands or storefronts under one verqo org — for marketplaces and multi-tenant operators." },
  { title: "EDI compliance · chain-retailer ready", desc: "Native EDI 850/810/856/940 with the major chains — no third-party translator." },
  { title: "ERP sync · NetSuite, SAP, QBE",      desc: "Auto-reconcile invoices, payments and AR into your accounting system of record." },
];

export const BUYER_ROADMAP = [
  { title: "Cross-border with trust",            desc: "Multi-currency, FX-locked, insurance that travels — first B2B BNPL built for global wholesale." },
  { title: "Buyer-led POs with demanded terms",  desc: "Issue a PO with the terms you want. Seller accepts, counters, or rejects in-portal." },
  { title: "Subordination flow",                 desc: "Upload subordination docs to override UCC / tax-lien blocks on a supplier." },
  { title: "Advanced Reports & Analytics",       desc: "Spend, velocity, supplier performance, category benchmarks." },
  { title: "Developer API & sandbox",            desc: "API keys, idempotency, sandbox mode, OpenAPI docs." },
  { title: "Discover suppliers on the network",  desc: "Find new wholesale brands credentialed on verqo — pre-vetted, ready to ship." },
];

/* ────────────────────────────────────────────────────────────────────── */
/* Pricing data                                                            */
/* ────────────────────────────────────────────────────────────────────── */
const PRICING_PLANS = [
  {
    id: "starter",
    name: "Starter",
    sub: "For founder-led brands just starting wholesale",
    monthlyPrice: 99,
    annualPrice: 89,
    buyersIncluded: 2,
    overageRate: 30,
    featured: false,
    eyebrow: null,
    bullets: [
      { bold: "2 active buyers included", text: " · then $30/buyer/mo" },
      { bold: "Custom domain", text: " on day 1" },
      { bold: "White-label", text: " buyer experience" },
      { text: "Unlimited orders · unlimited catalog" },
      { bold: "1 team seat (owner only)" },
      { text: "Payments · Credit · Shipping · Insurance — full access" },
      { text: "App Store access" },
      { text: "Webhooks + REST API · 60 req/min" },
      { text: "6-month data retention" },
      { text: "Self-serve onboarding" },
    ],
    heading: "Everything unlocked",
  },
  {
    id: "growth",
    name: "Growth",
    sub: "For wholesale brands with a team running one brand",
    monthlyPrice: 599,
    annualPrice: 539,
    buyersIncluded: 10,
    overageRate: 25,
    featured: true,
    eyebrow: "Most popular",
    bullets: [
      { bold: "10 active buyers included", text: " · then $25/buyer/mo" },
      { bold: "5 team seats" },
      { bold: "Remove 'Powered by verqo'", text: " branding" },
      { text: "Email support (biz hours)" },
      { text: "API at 600 req/min" },
      { text: "24-month data retention" },
      { text: "Custom invoice & email templates" },
      { text: "Full CSS customisation of buyer portal" },
      { text: "1 onboarding call included" },
      { text: "Beta feature access" },
    ],
    heading: "Everything in Starter, plus",
  },
  {
    id: "pro",
    name: "Pro",
    sub: "For scaled wholesale operators with multi-channel ops",
    monthlyPrice: 1499,
    annualPrice: 1349,
    buyersIncluded: 50,
    overageRate: 20,
    featured: false,
    eyebrow: null,
    bullets: [
      { bold: "50 active buyers included", text: " · then $20/buyer/mo" },
      { bold: "20 team seats" },
      { bold: "Multi-warehouse + multi-region" },
      { text: "Advanced reporting + custom dashboards" },
      { text: "API at 2,400 req/min" },
      { text: "48-month data retention" },
      { text: "Dedicated onboarding specialist" },
      { text: "Priority email + chat support" },
      { text: "Custom integrations available" },
      { text: "Volume pricing on transaction fees" },
    ],
    heading: "Everything in Growth, plus",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    sub: "For multi-brand or multi-warehouse operations",
    monthlyPrice: 3999,
    annualPrice: 3599,
    buyersIncluded: 100,
    overageRate: 15,
    featured: false,
    eyebrow: null,
    bullets: [
      { bold: "100 active buyers included", text: " · then $15/buyer/mo" },
      { bold: "Unlimited team seats" },
      { bold: "Unlimited warehouses" },
      { bold: "Unlimited brand entities" },
      { text: "Unlimited catalogs · unlimited data retention" },
      { text: "API at 10,000 req/min" },
      { bold: "Live chat + phone support", text: " (24/7)" },
      { bold: "Dedicated Customer Success Manager" },
      { text: "3 onboarding calls included" },
      { bold: "SSO + custom contract pricing" },
      { text: "Multi-currency · multi-language buyer portal" },
      { text: "Advanced analytics + AI demand forecasting" },
    ],
    heading: "Everything in Pro, plus",
  },
];

const TXN_ROWS = [
  { service: "Wholesale invoicing",       starter: "FREE",     growth: "FREE",     pro: "FREE",     enterprise: "FREE",     note: "No commission per invoice" },
  { service: "ACH · standard (next-day)", starter: "$0.50/tx", growth: "$0.50/tx", pro: "$0.50/tx", enterprise: "$0.50/tx", note: "Same flat fee on every plan" },
  { service: "ACH · same-day",            starter: "$3.50/tx", growth: "$3.50/tx", pro: "$3.50/tx", enterprise: "$3.50/tx", note: "Premium speed — posts within hours" },
  { service: "Wire transfer · domestic",  starter: "$15/tx",   growth: "$15/tx",   pro: "$15/tx",   enterprise: "$15/tx",   note: "Industry-standard wire fee" },
  { service: "Instant Payment Request",   starter: "$5/tx",    growth: "$5/tx",    pro: "$5/tx",    enterprise: "$5/tx",    note: "Real-time RFP — funds within 30 min" },
  { service: "Instant payout",            starter: "0.50%",    growth: "0.50%",    pro: "0.30%",    enterprise: "0.30%",    note: "Get paid in seconds vs Net-30/60/90", highlight: true },
  { service: "Credit card",               starter: "4%",       growth: "3%",       pro: "3%",       enterprise: "3%",       note: "Card-acceptance fee" },
];

const APP_MARKET = [
  { icon: TrendingUp,    name: "Advanced Analytics",   price: "$49",  desc: "Cohort analysis, custom dashboards, scheduled reports. Included with Scale." },
  { icon: Repeat,        name: "Auto-Replenish",       price: "$39",  desc: "Recurring/standing orders for cafés, vets, gyms." },
  { icon: Users,         name: "Sales-Rep Portal",     price: "$49",  desc: "Outside reps log in to manage their book and see commissions." },
  { icon: MessageSquare, name: "RFQ + Quote Engine",   price: "$79",  desc: "Buyers request quotes, you respond, convert to orders." },
  { icon: Database,      name: "QuickBooks Sync",      price: "$39",  desc: "Two-way sync with QuickBooks Online." },
  { icon: BadgePercent,  name: "Custom Buyer Pricing", price: "$39",  desc: "Different price lists per customer or customer group." },
  { icon: ShieldCheck,   name: "EDI Compliance",       price: "$99",  desc: "Walmart · Target · Whole Foods · UNFI · KeHE. Included with Scale." },
  { icon: Plug,          name: "ERP Sync",             price: "$199", desc: "NetSuite · SAP · QB Enterprise · Sage. Included with Scale." },
  { icon: Receipt,       name: "Trade-spend Tracker",  price: "$49",  desc: "BOGO, promos, slotting fees, off-invoice deductions. For food brands." },
  { icon: Shirt,         name: "Linesheet Builder",    price: "$49",  desc: "Size-matrix linesheets & seasonal catalogs. For apparel brands." },
  { icon: Lock,          name: "Compliance Vault",     price: "$39",  desc: "COA / MSDS / lot tracking. For beauty, food & wellness." },
  { icon: Calendar,      name: "Recurring Invoices",   price: "$29",  desc: "Run subscription-style billing for retainer customers." },
];

/* ENTERPRISE_FEATURES list removed — items absorbed into SELLER_ROADMAP (Feb 2026). */

/* ────────────────────────────────────────────────────────────────────── */
/* The six OS modules                                                     */
/* ────────────────────────────────────────────────────────────────────── */
const OS_MODULES = [
  { icon: ScrollText, name: "Orders",             blurb: "Every buyer PO parsed in seconds — PDF · email · CSV · Shopify · API. No more manual re-keying or chasing missing line items." },
  { icon: TrendingUp, name: "Buyer Intelligence", blurb: "Real-time buyer health. Live Buyer Score (0–850) — cash flow, sales velocity, payment history, business state. Updates every PO. Decide terms and ship with eyes open." },
  { icon: Brain,      name: "Credit",             blurb: "Your buyers walk in pre-approved. AI underwrites their credit, multi-funder pool covers the risk. You take orders without becoming a bank." },
  { icon: Banknote,   name: "Payments",           blurb: "Same-day cash into your wallet. Bank-grade ACH · wire rails wired directly. Your buyer pays Net-60. You pay nothing for the wait." },
  { icon: ShieldCheck,name: "Insurance",          blurb: "Sellers stay paid — period. Trade-credit cover (Allianz-underwritten) baked into every invoice. Insurance absorbs the loss." },
  { icon: Truck,      name: "Shipping",           blurb: "One portal for every shipment. Book parcel (UPS · FedEx · USPS) and freight. Rates, labels, tracking — all inside your verqo ledger." },
];

/* ────────────────────────────────────────────────────────────────────── */
/* Page                                                                   */
/* ────────────────────────────────────────────────────────────────────── */
const THEME_TOKENS = {
  tier1: { "--brand-fg": "#0c1612", "--brand-accent": "#dcff3a", "--page-bg": "#ffffff" },
  tier2: { "--brand-fg": "#0c1612", "--brand-accent": "#dcff3a", "--page-bg": "#FAFAF7" },
  tier3: { "--brand-fg": "#0c2417", "--brand-accent": "#dcff3a", "--page-bg": "#F8F7F2" },
};

const THEME_STYLES = `
  [data-theme="tier2"], [data-theme="tier3"] {
    background-color: var(--page-bg) !important;
  }
  [data-theme="tier2"] .rounded-2xl, [data-theme="tier3"] .rounded-2xl { border-radius: 14px !important; }
  [data-theme="tier2"] .rounded-xl,  [data-theme="tier3"] .rounded-xl  { border-radius: 12px !important; }
  [data-theme="tier2"] .rounded-lg,  [data-theme="tier3"] .rounded-lg  { border-radius: 8px !important; }
  [data-theme="tier2"] [data-serif], [data-theme="tier3"] [data-serif] {
    font-family: "Instrument Serif", Georgia, serif !important;
    font-style: italic;
    font-weight: 400;
    letter-spacing: -0.015em;
  }
  [data-theme="tier2"]::before, [data-theme="tier3"]::before {
    content: "";
    position: fixed; inset: 0; pointer-events: none; z-index: 30;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E");
    opacity: 0.035; mix-blend-mode: multiply;
  }
  [data-theme="tier3"] { cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Ccircle cx='10' cy='10' r='4' fill='%23dcff3a' stroke='%230c2417' stroke-width='1.5'/%3E%3C/svg%3E") 10 10, auto; }

  /* Hero rotating word — quick slide-up swap (Feb 2026).
     220ms ensures the text is never visible mid-fade for long enough
     to look like a loading state; starts at 90% opacity (not 0) so the
     reader never sees a blank/grey flash. */
  @keyframes verqo-word-rotate {
    0%   { opacity: 0.05; transform: translateY(8px) rotate(-1deg); }
    100% { opacity: 1;    transform: translateY(0)   rotate(-1deg); }
  }
  .verqo-word-rotate {
    animation: verqo-word-rotate 0.22s ease-out;
  }
`;

export default function LandingV2Preview({ theme = "tier1", variant = "full", hideBanner = false, buyerHeroV2 = false, heroVariant = null, trimmedForPreview = false, hideHeader = false, hideFooter = false, hideNonEssentialSections = false, hidePricing = false }) {
  // `heroVariant` (Feb 2026) — when set to a key from HERO_VARIANTS, the
  // default hero section is replaced with that alternative design. Used
  // by /preview/hero/* routes to compare 6 hero designs in real-page
  // context without forking the entire landing component.
  const { scrollYProgress } = useScroll();
  // openFaq state removed Feb 2026 — FAQ now lives at the dedicated /faq route.
  const [billing, setBilling] = useState("monthly");
  // Hero — rotating headline highlight (Feb 2026, replaces static "60 days")
  // Cycles through the 4 verqo wholesale value props every 2.5s. Keeps the
  // existing diagram, stats, checkmarks, CTAs all untouched.
  const HERO_ROTATING_WORDS = [
    "paid the moment you sell.",
    "with eyes on every buyer.",
    "covered on every invoice.",
    "fully automated, end-to-end.",
  ];
  const [heroWordIdx, setHeroWordIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroWordIdx((x) => (x + 1) % HERO_ROTATING_WORDS.length), 2500);
    return () => clearInterval(t);
  }, []);
  const heroRotatingWord = HERO_ROTATING_WORDS[heroWordIdx];
  // Variant gates the long narrative sections so we can compare structures
  // side-by-side without touching the live page.
  //   full       — current live homepage (default)
  //   plan-a     — drops Problem AND Solution sections
  //   plan-c     — drops Problem only (keeps Solution)
  //   plan-ac    — keeps both (baseline · same as full for these sections)
  const showProblem  = variant === "full" || variant === "plan-ac" || variant === "plan-a-lean";
  const showSolution = variant === "full" || variant === "plan-c" || variant === "plan-ac";
  const isLean       = variant === "plan-a-lean";
  const previewLabel = {
    "plan-a":      "PLAN A · Drop Problem + Solution",
    "plan-c":      "PLAN C · Drop Problem only · keep Solution",
    "plan-ac":     "PLAN A+C · Keep Problem AND Solution",
    "plan-a-lean": "PLAN A-LEAN · Drop Problem + Solution + 4 polish cuts",
  }[variant];
  useEffect(() => { document.title = "verqo · Wholesale, instant."; }, []);

  return (
    <div
      className="min-h-screen text-slate-900 antialiased selection:bg-lime-300 selection:text-slate-900"
      data-theme={theme}
      data-testid="landing-v2"
      data-lean={isLean ? "true" : undefined}
      data-trimmed-preview={trimmedForPreview ? "true" : undefined}
      data-hide-header={(hideHeader || trimmedForPreview) ? "true" : undefined}
      data-hide-nonessential={(hideNonEssentialSections || trimmedForPreview) ? "true" : undefined}
      style={{ ...THEME_TOKENS[theme], background: "var(--page-bg)" }}
    >
      <style>{THEME_STYLES}</style>

      {isLean && (
        <style>{`
          /* === Plan A-Lean · Spacing Pass === */
          /* Tighten section padding ~30% so the page reads more like Stripe/Linear */
          [data-lean="true"] section[id="how"],
          [data-lean="true"] section[data-testid="section-only-on-verqo-sellers"],
          [data-lean="true"] section[data-testid="section-for-buyers"],
          [data-lean="true"] section[data-testid="section-only-on-verqo-buyers"],
          [data-lean="true"] section[id="pricing"],
          [data-lean="true"] section[data-testid="section-compare"],
          [data-lean="true"] section[data-testid="section-coming-soon"],
          [data-lean="true"] section[data-testid="section-faq"],
          [data-lean="true"] section[id="cta"] {
            padding-top: 3.5rem !important;
            padding-bottom: 3.5rem !important;
          }
          @media (min-width: 768px) {
            [data-lean="true"] section[id="how"],
            [data-lean="true"] section[data-testid="section-only-on-verqo-sellers"],
            [data-lean="true"] section[data-testid="section-for-buyers"],
            [data-lean="true"] section[data-testid="section-only-on-verqo-buyers"],
            [data-lean="true"] section[id="pricing"],
            [data-lean="true"] section[data-testid="section-compare"],
            [data-lean="true"] section[data-testid="section-coming-soon"],
            [data-lean="true"] section[data-testid="section-faq"],
            [data-lean="true"] section[id="cta"] {
              padding-top: 5rem !important;
              padding-bottom: 5rem !important;
            }
          }
          /* CTA section was extra-tall — tighten more aggressively */
          [data-lean="true"] section[id="cta"] {
            padding-top: 4rem !important;
            padding-bottom: 4rem !important;
          }
          @media (min-width: 768px) {
            [data-lean="true"] section[id="cta"] {
              padding-top: 6rem !important;
              padding-bottom: 6rem !important;
            }
          }
          /* === Background rebalance — strict alternation so no two adjacent sections share bg === */
          [data-lean="true"] section[id="how"] {
            background: #ffffff !important;
            border-top-color: transparent !important;
            border-bottom-color: transparent !important;
          }
          [data-lean="true"] section[data-testid="section-only-on-verqo-sellers"] {
            background: rgb(248 250 252 / 0.5) !important;
            border-top: 1px solid rgb(241 245 249) !important;
            border-bottom: 1px solid rgb(241 245 249) !important;
          }
          [data-lean="true"] section[data-testid="section-faq"] {
            background: rgb(248 250 252 / 0.5) !important;
            border-top: 1px solid rgb(241 245 249) !important;
            border-bottom: 1px solid rgb(241 245 249) !important;
          }
        `}</style>
      )}

      {(trimmedForPreview || hideHeader || hideNonEssentialSections) && (
        <style>{`
          /* Feb 2026 · Nav-integration mode
             - data-hide-nonessential: hides sections destined for sub-pages
             - data-hide-header: adjusts hero top padding when external mega-menu is used
          */
          [data-hide-nonessential="true"] section[data-testid="section-portals"],
          [data-hide-nonessential="true"] section[data-testid="section-compare"],
          [data-hide-nonessential="true"] section[data-testid="section-coming-soon"] {
            display: none !important;
          }
          [data-hide-header="true"] section[data-testid="section-hero"] {
            padding-top: 5rem !important;
          }
          @media (min-width: 768px) {
            [data-hide-header="true"] section[data-testid="section-hero"] {
              padding-top: 4rem !important;
            }
          }
        `}</style>
      )}

      {previewLabel && !hideBanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-300 text-[11px] uppercase tracking-[0.2em] text-amber-800 shadow-lg" data-testid="preview-variant-banner">
          <CircleDot className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
          {previewLabel}
        </div>
      )}

      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-lime-500 z-50 origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Floating nav — ORIGINAL brand lockup restored
          Suppressed when using the external mega-menu header (live `/`)
          or in the older trimmed-preview mode. */}
      {!(trimmedForPreview || hideHeader) && (
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2 cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 rounded-md"
            aria-label="Back to top"
            data-testid="nav-brand-home"
          >
            <VerqoLockup />
            <span className="ml-1 text-[11px] text-slate-500 hidden lg:inline group-hover:text-slate-900 transition-colors" data-testid="nav-tagline">· Wholesale, instant.</span>
          </button>
          <nav className="hidden lg:flex items-center gap-7 text-[12px] text-slate-600">
            <a href="#problem"    data-testid="nav-platform-link"  className="hover:text-slate-900 transition-colors">Platform</a>
            <a href="#how"        data-testid="nav-how-link"       className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="#why-verqo"  data-testid="nav-why-link"       className="hover:text-slate-900 transition-colors">Why verqo</a>
            <a href="#pricing"    data-testid="nav-pricing-link"   className="hover:text-slate-900 transition-colors">Pricing</a>
            <a href="/dropship"   data-testid="nav-dropship-link"  className="hover:text-slate-900 transition-colors">
              Dropship
            </a>
            <a href="/buyers" data-testid="nav-buyers-link"    className="text-slate-500 hover:text-slate-900 transition-colors">For buyers</a>
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <a href="/login?as=seller" data-testid="nav-signin" className="text-[12px] text-slate-600 hover:text-slate-900 transition-colors">Sign in</a>
            <a href="/waitlist?as=seller" data-testid="nav-cta-get-paid" className="text-[12px] px-4 py-2 rounded-md text-white hover:opacity-90 transition-opacity font-medium" style={{ background: "var(--brand-fg)" }}>Get paid today</a>
          </div>
          <a href="/waitlist?as=seller" data-testid="nav-cta-get-paid-mobile" className="lg:hidden text-[12px] px-3.5 py-2 rounded-md text-white hover:opacity-90 transition-opacity font-medium" style={{ background: "var(--brand-fg)" }}>Get paid</a>
          <MarketingMobileNav
            items={[
              { label: "Platform",     href: "#problem" },
              { label: "How it works", href: "#how" },
              { label: "Why verqo",    href: "#why-verqo" },
              { label: "Pricing",      href: "#pricing" },
              { label: "Dropship", href: "/dropship" },
              { label: "For buyers",   href: "/buyers" },
              { label: "About",        href: "/about" },
              { label: "Partners",     href: "/partners" },
              { label: "FAQ",          href: "/faq" },
              { label: "Contact",      href: "/contact" },
            ]}
            cta={{ label: "Get paid today", href: "/waitlist?as=seller" }}
          />
        </div>
      </header>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 1 — HERO · the wholesale-OS thesis                         */}
      {/* If heroVariant is supplied, render the alternative hero design     */}
      {/* instead of the default. See /preview/heroes for the gallery.       */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {heroVariant && HERO_VARIANTS[heroVariant] ? (() => {
        const HeroComp = HERO_VARIANTS[heroVariant];
        return <HeroComp/>;
      })() : (
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-hero">
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy + CTAs */}
          <div>
            <Reveal>
              <Eyebrow testid="hero-eyebrow">For brands, wholesalers &amp; distributors · $1M–$100M</Eyebrow>
            </Reveal>

            <Reveal delay={0.08}>
              <h1 className="mt-6 text-[36px] sm:text-[44px] md:text-[68px] lg:text-[80px] leading-[1.02] sm:leading-[0.98] md:leading-[0.96] tracking-tight font-semibold text-slate-900" data-testid="hero-headline">
                Wholesale,<br />
                <span key={heroWordIdx} className="bg-lime-300 px-2.5 sm:px-3 -rotate-1 inline-block my-2 sm:my-3 verqo-word-rotate" data-testid="hero-rotating-word">
                  {heroRotatingWord}
                </span>
              </h1>
            </Reveal>

            <Reveal delay={0.22}>
              <p className="mt-8 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600" data-testid="hero-subhead">
                Run your entire wholesale business from one portal: <strong className="text-slate-900">Day 1 payouts, Insurance-backed cover, real-time buyer monitoring, and end-to-end automation.</strong>
              </p>
            </Reveal>

            <Reveal delay={0.32}>
              <div className="mt-9 flex flex-wrap gap-3 items-center">
                <a href="/waitlist?as=seller" data-testid="cta-hero-primary" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all" style={{ background: "var(--brand-fg)" }}>
                  Get paid today
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="#how" data-testid="cta-hero-secondary" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
                  See how it works
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.44}>
              <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-[12px] text-slate-500">
                <div className="inline-flex items-center gap-1.5" data-testid="hero-promise-1"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Same-day cash on every invoice</div>
                <div className="inline-flex items-center gap-1.5" data-testid="hero-promise-2"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Zero default risk — Allianz insured</div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — animated network graph in a dark "product preview" card */}
          <Reveal delay={0.15} y={32}>
            <div className="relative rounded-2xl p-6 md:p-7 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.4)]" style={{ background: "var(--brand-fg)" }} data-testid="hero-network-card">
              <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
              />
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: "radial-gradient(circle at 50% 40%, rgba(220,255,58,0.18) 0%, transparent 55%)" }}
              />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.26em] font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--brand-accent)" }} data-testid="hero-card-eyebrow">
                    <span aria-hidden="true">✦</span>
                    The operating system for wholesale
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse" /> Live
                  </div>
                </div>
                <NetworkGraph />
                <div className="mt-2 text-center">
                  <div className="text-[19px] md:text-[21px] font-semibold text-white tracking-tight leading-tight" data-testid="hero-card-caption">
                    Everything wholesale needs to <span className="text-lime-300">sell, get paid, and ship</span> <span className="text-white/45">— in one platform.</span>
                  </div>
                  <div className="text-[11.5px] text-white/55 mt-1.5 max-w-[340px] mx-auto leading-relaxed">
                    Buyer · seller · lender · insurer · bank · carrier — every player, every step, in real time.
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Big honest number strip — placeholders, swap with real numbers on migration */}
        {!isLean && (
        <Reveal delay={0.55}>
          <div className="mt-16 pt-8 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10" data-testid="hero-stats">
            {(isLean ? [
              ["500+",   "buyers credentialed on day 1"],
              ["<60 sec", "credit decisions"],
            ] : [
              ["500+",            "buyers credentialed on day 1"],
              ["Net-30/60/90/120", "approved in seconds"],
              ["<60 sec",          "credit decisions"],
              ["0 days",           "DSO on funded invoices"],
            ]).map(([v, l]) => (
              <div key={l}>
                <div className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{v}</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-1.5">{l}</div>
              </div>
            ))}
          </div>
        </Reveal>
        )}
      </section>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 1.5 — WHO IT'S FOR (self-qualification band)               */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative pt-2 pb-14 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-industries">
        {!isLean && (
        <Reveal>
          <div className="text-center">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 mb-3 inline-flex items-center gap-2">
              <CircleDot className="w-2.5 h-2.5" /> Who it&apos;s for
            </div>
            <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900 max-w-2xl mx-auto leading-snug">
              Brands, wholesalers &amp; distributors doing <span className="bg-lime-100 px-1.5 -rotate-[0.5deg] inline-block">$1M–$100M</span> in revenue.
            </h3>
            <p className="mt-3 text-[13.5px] text-slate-500 max-w-xl mx-auto leading-relaxed">
              Built for these categories — not for 100+ SKU enterprise distributors on SAP / Oracle / NetSuite.
            </p>
          </div>
        </Reveal>
        )}
        <Reveal delay={isLean ? 0 : 0.08}>
          {isLean && (
            <p className="text-center text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-5">
              Built for these categories
            </p>
          )}
          <div className={`${isLean ? "" : "mt-8 "}flex flex-wrap items-center justify-center gap-2.5 md:gap-3`} data-testid="industries-strip">
            {INDUSTRIES.map((i) => (
              <div
                key={i.name}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white hover:border-lime-400 hover:bg-lime-50/40 hover:-translate-y-0.5 transition-all"
                data-testid={`industry-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <i.icon className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.8} />
                <span className="text-[12.5px] font-medium text-slate-700">{i.name}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 2 — TRUSTED BY (removed Feb-2026: logos > $100M ICP)        */}
      {/* ═════════════════════════════════════════════════════════════════ */}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 2.5 — (industries moved above customers, see Section 1.5) */}
      {/* ═════════════════════════════════════════════════════════════════ */}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 3 — THE PROBLEM · both sides + the fragmentation           */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {showProblem && (
      <section id="problem" className="relative pt-6 md:pt-10 pb-8 md:pb-12 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-problem">
        <Reveal>
          <div className="max-w-3xl">
            <Eyebrow>The problem</Eyebrow>
            <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight leading-[1.02] text-slate-900">
              Wholesale is broken on <span className="text-slate-400">both sides</span> of every transaction.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-600 max-w-2xl">
              Sellers are starved of cash. Buyers are starved of credit. And the seven vendors
              between them don't talk to each other. Here's exactly what's breaking — and how verqo fixes it.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="problem-grid">
          {/* SELLER PAIN */}
          <Reveal delay={0.05}>
            <div className="rounded-2xl border border-rose-200/60 bg-rose-50/30 p-7 md:p-9 h-full">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-rose-100 grid place-items-center">
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-rose-700 font-semibold">What sellers struggle with</div>
              </div>
              <div className="space-y-4">
                {[
                  ["60-90 days waiting on your money", "Cash is frozen. Inventory turn collapses. Working capital becomes the ceiling on growth."],
                  ["No buyer visibility",     "You can't see if your buyer just defaulted on three other suppliers last week."],
                  ["Manual credit decisions", "Every new buyer = paper trade references, spreadsheet underwriting, multi-week delays."],
                  ["Chasing payments",        "Dunning emails, write-offs, collections — work that pays nothing."],
                  ["Five systems to ship one order", "Order in Shopify · invoice in QuickBooks · payment in Stripe · carrier in UPS · insurance somewhere else."],
                ].map(([h, b]) => (
                  <div key={h} className="flex gap-3">
                    <div className="w-1 h-5 rounded-full bg-rose-400 mt-1 flex-shrink-0" />
                    <div>
                      <div className="text-[14px] font-semibold text-slate-900">{h}</div>
                      <div className="text-[12.5px] leading-relaxed text-slate-600 mt-1">{b}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-7 pt-5 border-t border-rose-200/60">
                <div className="flex items-start gap-2 text-[12.5px] text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 flex-shrink-0" />
                  <span><strong>verqo fixes it.</strong> Day-0 settlement · AI credit decisions in seconds · auto-debit · insurance takeover · every system in one ledger.</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* BUYER PAIN */}
          <Reveal delay={0.12}>
            <div className="rounded-2xl border border-amber-200/60 bg-amber-50/30 p-7 md:p-9 h-full">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-amber-100 grid place-items-center">
                  <AlertCircle className="w-4 h-4 text-amber-700" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-800 font-semibold">What buyers struggle with</div>
              </div>
              <div className="space-y-4">
                {[
                  ["Re-applying at every supplier", "Net-30 with one supplier means nothing to the next. Same paperwork, every single time."],
                  ["2-6 week onboarding",           "Paper trade references, fax-back forms, credit-officer review. Wholesale runs at the speed of fax."],
                  ["Late fees + credit holds",      "One missed invoice = order shipments paused. Your operations stop because finance is fragmented."],
                  ["No portable trust",             "Your perfect payment history at Supplier A doesn't unlock terms at Supplier B."],
                  ["Cash-flow ceiling on growth",   "You can't buy more inventory because you don't have credit — even though you've earned it."],
                ].map(([h, b]) => (
                  <div key={h} className="flex gap-3">
                    <div className="w-1 h-5 rounded-full bg-amber-400 mt-1 flex-shrink-0" />
                    <div>
                      <div className="text-[14px] font-semibold text-slate-900">{h}</div>
                      <div className="text-[12.5px] leading-relaxed text-slate-600 mt-1">{b}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-7 pt-5 border-t border-amber-200/60">
                <div className="flex items-start gap-2 text-[12.5px] text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 flex-shrink-0" />
                  <span><strong>verqo fixes it.</strong> One credit line · portable across every supplier on verqo · approved in seconds · AI recalibrates as you grow.</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* And the meta problem — fragmentation */}
        <Reveal delay={0.2}>
          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-7 md:p-9" data-testid="fragmentation-card">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">
              <div className="lg:w-[40%]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-900 grid place-items-center">
                    <Network className="w-4 h-4 text-lime-300" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-700 font-semibold">And the problem nobody talks about</div>
                </div>
                <div className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900 leading-snug">
                  Seven disconnected vendors. <span className="text-slate-400">Zero source of truth.</span>
                </div>
              </div>
              <div className="lg:flex-1">
                <div className="text-[13.5px] leading-relaxed text-slate-600">
                  The average wholesaler stitches together <strong className="text-slate-800">QuickBooks · Shopify · Stripe · a BNPL · a freight carrier · an insurance broker · a sponsor bank</strong>.
                  None of them agree on the same row of data. Reconciliation eats hours every week.
                  Disputes take days. Examiners cost months.
                </div>
                <div className="mt-4 text-[13px] text-slate-700">
                  <strong>verqo replaces all seven</strong> — one ledger, one API, every party on the same row.
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* "Your buyers love it too" — handles the seller's #1 adoption objection */}
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl border border-lime-300 bg-lime-50 p-7 md:p-9" data-testid="buyer-adoption-callout">
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5 lg:gap-10 items-start lg:items-center">
              <div className="lg:max-w-[300px]">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 font-semibold mb-2.5">Your buyers love it too</div>
                <div className="text-lg md:text-xl font-semibold text-slate-900 leading-snug">
                  Your buyers won't just adopt it. <span className="text-slate-500">They'll prefer it.</span>
                </div>
              </div>
              <div className="text-[13.5px] leading-relaxed text-slate-700">
                Buyers get <strong className="text-slate-900">one credit line that works at every supplier on verqo</strong> — approved in seconds, no spreadsheets, no re-applying, no paper trade references.
                Which means <strong className="text-slate-900">they buy from you more often, with bigger baskets.</strong>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 4 — THE SOLUTION · the six OS modules                      */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {showSolution && (
      <section id="solution" className="relative py-20 md:py-28 px-6 md:px-12 bg-slate-50/40 border-y border-slate-100" data-testid="section-solution">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>The solution · the wholesale OS</Eyebrow>
              <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight leading-[1.02] text-slate-900">
                Six modules. <span className="text-slate-400">One ledger.</span> <span className="text-slate-400">Every party.</span>
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-slate-600 max-w-2xl">
                verqo is the first complete operating system built for the <strong className="text-slate-900">six players who actually run wholesale</strong> — buyer, seller, lender, insurer, bank, carrier.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="os-modules">
            {OS_MODULES.map((m, i) => (
              <Reveal key={m.name} delay={(i % 3) * 0.06}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white hover:border-lime-400 hover:shadow-[0_24px_64px_-24px_rgba(132,204,22,0.25)] transition-all p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "var(--brand-fg)" }}>
                      <m.icon className="w-4 h-4" style={{ color: "var(--brand-accent)" }} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-400">Module {i + 1} / 6</div>
                      <div className="text-base font-semibold text-slate-900">{m.name}</div>
                    </div>
                  </div>
                  <div className="text-[13px] leading-relaxed text-slate-600">{m.blurb}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 5 (merged) — HOW IT WORKS · LIVE IN THE PORTAL              */}
      {/* Replaces the old FlowDiagram-only section. The tabbed switcher       */}
      {/* below now serves as both the "how it works" walkthrough AND          */}
      {/* the live product proof.                                              */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section id="how" className="relative py-16 md:py-24 bg-slate-50/40 border-y border-slate-100" data-testid="section-portals">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>How it works · live in the portal</Eyebrow>
              <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight leading-[1.02] text-slate-900">
                From PO to paid. <span className="text-slate-400">In four clicks.</span>
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-slate-600 max-w-2xl">
                Your wholesale flow, unchanged — except the cash hits your wallet on <strong className="text-slate-900">day 0, not day 60</strong>. Click any step or let it auto-play.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Tabbed switcher — list of 5 steps on left, sticky screenshot on right */}
        <div className="mt-12 max-w-[1280px] mx-auto px-6 md:px-12">
          <InlinePortalTabs scenes={SELLER_SCENES} testid="landing-seller-tabs" />
        </div>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 5.5 — ONLY ON VERQO · SELLER MOATS                         */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section id="why-verqo" className="relative pt-20 md:pt-28 pb-16 md:pb-20 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-only-on-verqo-sellers">
        <Reveal>
          <Eyebrow testid="seller-moats-eyebrow">Only on verqo · for sellers</Eyebrow>
        </Reveal>
        <Reveal delay={0.06}>
          <h2 className="mt-5 text-3xl md:text-5xl leading-[1.02] tracking-tight font-semibold text-slate-900 max-w-3xl">
            Beyond same-day cash:<br /> eight things <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">no bank or BNPL</span> gives you.
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 text-[15px] md:text-[17px] text-slate-600 max-w-2xl leading-relaxed">
            The 60-day pain is the hook. But verqo is the <strong className="text-slate-900">wholesale operating system</strong> — order, payment, shipping, and reconciliation, all on one ledger.
          </p>
        </Reveal>

        <div
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[1fr]"
          data-testid="seller-moat-bento"
        >
          {SELLER_MOAT_TILES.map((t, i) => (
            <MoatTile
              key={`smoat-${i}`}
              n={i + 1}
              icon={t.icon}
              title={t.title}
              body={t.body}
              variant={t.variant}
              testid={`seller-moat-tile-${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 6 — FOR BUYERS · audience section                          */}
      {/* When buyerHeroV2 is true, swap in the V2 hero with the              */}
      {/* 1-to-Many "portable credit" graphic on the right.                   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {buyerHeroV2 ? null : (
      <>
      <section id="for-buyers" className="relative py-20 md:py-28 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-for-buyers">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-start">
            {/* Left: hero copy */}
            <div>
              <Eyebrow testid="for-buyers-eyebrow">For wholesale buyers</Eyebrow>
              <h2 className="mt-5 text-[36px] md:text-[56px] leading-[0.98] font-semibold tracking-tight text-slate-900">
                Stop paying <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">cash upfront.</span><br />
                <span className="text-slate-400">Buy on terms. Everywhere.</span>
              </h2>
              <p className="mt-6 max-w-xl text-[15px] md:text-[17px] leading-relaxed text-slate-600">
                Apply once. Get pre-approved for <strong className="text-slate-900">Net-30/60/90</strong> in 60 seconds. Use it at every supplier on verqo — or <strong className="text-slate-900">bring your existing suppliers on with one PO.</strong>
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href="/waitlist?as=buyer" data-testid="for-buyers-cta-primary" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all" style={{ background: "var(--brand-fg)" }}>
                  Get pre-approved — free
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="/login?as=buyer" data-testid="for-buyers-signin" className="text-[13.5px] text-slate-600 hover:text-slate-900 transition-colors">
                  Already on verqo? Sign in →
                </a>
              </div>
              <div className="mt-6 text-[12px] text-slate-500">
                Buyers pay <strong className="text-slate-700">$0</strong> to verqo. Always. Suppliers cover the cost.
              </div>
            </div>

            {/* Right: 3 proof bullets in a column */}
            <div className="space-y-4" data-testid="for-buyers-bullets">
              {[
                { i: Sparkles,      h: "Get pre-approved first.",              b: "60-second decision. Use your line at any supplier on verqo — or use the proof to negotiate harder with suppliers off-network." },
                { i: FileSignature, h: "Bring your suppliers — on your terms.", b: "Issue a PO with the payment terms you want. Your supplier accepts, counters, or joins verqo to fulfill it. Either way, you win." },
                { i: BadgePercent,  h: "Pay $0 — always.",                     b: "No subscription. No application fees. Suppliers cover the cost — you keep all the leverage." },
              ].map(({ i: Icon, h, b }) => (
                <div key={h} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-lime-300 hover:-translate-y-0.5 transition-all" data-testid={`for-buyers-bullet-${h.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`}>
                  <div className="w-9 h-9 rounded-lg bg-lime-100 grid place-items-center">
                    <Icon className="w-4 h-4 text-lime-700" />
                  </div>
                  <div className="mt-4 text-[15px] font-semibold text-slate-900">{h}</div>
                  <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 6.25 — BUYER · TWO PATHS (chicken-and-egg objection killer) */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-buyer-paths">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Eyebrow testid="buyer-paths-eyebrow">Either way, you win</Eyebrow>
            <h3 className="mt-5 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 leading-snug">
              Two ways to win on verqo.
            </h3>
            <p className="mt-3 text-[13.5px] md:text-[14.5px] text-slate-600 leading-relaxed max-w-xl mx-auto">
              You don't have to wait for the network to come to you. Pull your suppliers in — or pick from the ones already here.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="buyer-paths-grid">
          {/* Path 1 — suppliers already on verqo */}
          <Reveal delay={0.05}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 md:p-8 hover:border-lime-300 hover:-translate-y-0.5 transition-all" data-testid="buyer-path-existing">
              <div className="w-10 h-10 rounded-lg bg-lime-100 grid place-items-center mb-5">
                <Plug className="w-4 h-4 text-lime-700" />
              </div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 font-semibold mb-2">Path 1</div>
              <h4 className="text-lg md:text-xl font-semibold text-slate-900 leading-snug">
                My suppliers are <span className="text-slate-400">already on verqo.</span>
              </h4>
              <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                Connect in one click. Place your next PO with <strong className="text-slate-900">Net-30/60/90 baked in.</strong> Goods release the moment you hit pay — no wire, no ACH delay.
              </p>
              <a href="/waitlist?as=buyer" data-testid="buyer-path-existing-cta" className="group mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 hover:text-lime-700 transition-colors">
                See suppliers on verqo
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </Reveal>

          {/* Path 2 — bring your suppliers in */}
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 md:p-8 hover:border-lime-300 hover:-translate-y-0.5 transition-all" data-testid="buyer-path-new">
              <div className="w-10 h-10 rounded-lg bg-lime-100 grid place-items-center mb-5">
                <FileSignature className="w-4 h-4 text-lime-700" />
              </div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 font-semibold mb-2">Path 2</div>
              <h4 className="text-lg md:text-xl font-semibold text-slate-900 leading-snug">
                My suppliers <span className="text-slate-400">aren't on verqo yet.</span>
              </h4>
              <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                Send your supplier a verqo-issued PO with <strong className="text-slate-900">the payment terms you want.</strong> They onboard in minutes to fulfill it — and you're now their preferred buyer.
              </p>
              <a href="/waitlist?as=buyer" data-testid="buyer-path-new-cta" className="group mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 hover:text-lime-700 transition-colors">
                Invite your supplier
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 6.5 — ONLY ON VERQO · BUYER MOATS                          */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 bg-slate-50/50 border-y border-slate-100" data-testid="section-only-on-verqo-buyers">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <Eyebrow testid="buyer-moats-eyebrow">Only on verqo · for buyers</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-5 text-3xl md:text-5xl leading-[1.02] tracking-tight font-semibold text-slate-900 max-w-3xl">
              Eight things <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">retailers and resellers</span><br /> have been waiting for.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-[15px] md:text-[17px] text-slate-600 max-w-2xl leading-relaxed">
              One portable credit line. Sixty-second approvals. Goods released the moment you click pay. Wholesale buying without the paper, the phone calls, or the wire delays.
            </p>
          </Reveal>

          <div
            className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[1fr]"
            data-testid="buyer-moat-bento"
          >
            {BUYER_MOAT_TILES.map((t, i) => (
              <MoatTile
                key={`bmoat-${i}`}
                n={i + 1}
                icon={t.icon}
                title={t.title}
                body={t.body}
                variant={t.variant}
                testid={`buyer-moat-tile-${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
      </>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 7 — PRICING (moved to the dedicated /pricing page Jun 2026) */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {!hidePricing && (
      <section id="pricing" className="relative py-20 md:py-28 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-pricing">
        {/* Header */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight leading-[1.02] text-slate-900">
              One platform. <span className="text-slate-400 italic">Four simple plans.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-600">
              Built for wholesale brands moving real volume — beauty, food, coffee, pet, wellness.
              Easy entrance. Pay only for the apps and services you use. <strong className="text-slate-900">Buyers are free, forever.</strong>
            </p>
          </div>
        </Reveal>

        {/* Billing toggle */}
        <Reveal delay={0.05}>
          <div className="mt-10 flex items-center justify-center gap-3" data-testid="pricing-billing-toggle">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              data-testid="billing-monthly"
              className={`px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all ${
                billing === "monthly" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling("annual")}
              data-testid="billing-annual"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all ${
                billing === "annual" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Annual
              <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full ${
                billing === "annual" ? "bg-lime-300 text-slate-900" : "bg-lime-100 text-lime-800"
              }`}>Save 10%</span>
            </button>
          </div>
        </Reveal>

        {/* Plan cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-[1300px] mx-auto" data-testid="pricing-tiers">
          {PRICING_PLANS.map((t, i) => {
            const shownPrice = billing === "annual" ? t.annualPrice : t.monthlyPrice;
            return (
              <Reveal key={t.id} delay={i * 0.07}>
                <div
                  data-testid={`pricing-card-${t.id}`}
                  className={`h-full rounded-2xl p-7 md:p-8 flex flex-col ${
                    t.featured
                      ? "border-2 bg-white shadow-[0_24px_64px_-24px_rgba(132,204,22,0.35)] -translate-y-2"
                      : "border border-slate-200 bg-white"
                  }`}
                  style={t.featured ? { borderColor: "var(--brand-fg)" } : undefined}
                >
                  {t.eyebrow && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold w-fit mb-4" style={{ background: "var(--brand-accent)", color: "var(--brand-fg)" }}>
                      <Star className="w-3 h-3" /> {t.eyebrow}
                    </div>
                  )}
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{t.name}</div>
                  <div className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">{t.sub}</div>

                  <div className="mt-6 flex items-baseline gap-1.5">
                    <span className="text-[44px] font-semibold tracking-tight text-slate-900 leading-none">${shownPrice}</span>
                    <span className="text-[13px] text-slate-500">/ month</span>
                    {billing === "annual" && (
                      <span className="ml-2 text-[11px] text-lime-700 bg-lime-50 border border-lime-200 px-1.5 py-0.5 rounded-full">billed annually</span>
                    )}
                  </div>
                  <div className="mt-2 text-[12px] text-slate-500">
                    {billing === "annual" ? "Save 10% · cancel anytime" : "Billed monthly · cancel anytime"}
                  </div>
                  {t.buyersIncluded && (
                    <div className="mt-3 text-[12.5px] text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid={`buyer-pricing-${t.id}`}>
                      <strong>{t.buyersIncluded} active buyers</strong> included
                      <span className="text-slate-500"> · then <strong className="text-slate-700">${t.overageRate}</strong>/buyer/mo</span>
                      {billing === "annual" && (
                        <span className="text-slate-400"> · no discount on buyer overages</span>
                      )}
                    </div>
                  )}

                  <a
                    href="/waitlist?as=seller"
                    data-testid={`pricing-cta-${t.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors ${
                      t.featured ? "text-white hover:opacity-90" : "bg-white border border-slate-300 text-slate-900 hover:bg-slate-50"
                    }`}
                    style={t.featured ? { background: "var(--brand-fg)" } : undefined}
                  >
                    Join waitlist
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>

                  <div className="mt-7 pt-6 border-t border-slate-200">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">{t.heading}</div>
                    <ul className="space-y-2.5 flex-1">
                      {t.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                          <CheckCircle2 className="w-3.5 h-3.5 text-lime-600 mt-0.5 flex-shrink-0" />
                          <span>
                            {b.bold && <strong className="text-slate-900">{b.bold}</strong>}
                            {b.text || ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Transaction pricing table */}
        <Reveal delay={0.15}>
          <div className="mt-24 max-w-[1100px] mx-auto" data-testid="pricing-txn-table">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">Transaction pricing</h3>
              <p className="mt-4 text-[14px] text-slate-600 leading-relaxed">
                No per-invoice commissions like the marketplaces. Pay only for the rails you use — and lower as you grow.
              </p>
            </div>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-[0_24px_64px_-32px_rgba(15,23,42,0.18)]">
              <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1.5fr] border-b border-slate-200 bg-slate-50/60">
                <div className="px-5 py-4 text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Service</div>
                <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Starter</div>
                <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 bg-lime-50/60">Growth</div>
                <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Pro</div>
                <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Enterprise</div>
                <div className="px-4 py-4 text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Notes</div>
              </div>
              {TXN_ROWS.map((r, i) => (
                <div
                  key={r.service}
                  className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1.5fr] ${i < TXN_ROWS.length - 1 ? "border-b border-slate-100" : ""} ${r.highlight ? "bg-lime-50/40" : ""}`}
                  data-testid={`txn-row-${i}`}
                >
                  <div className={`px-5 py-4 text-[13px] font-semibold ${r.highlight ? "text-slate-900" : "text-slate-800"}`}>{r.service}</div>
                  <div className="px-4 py-4 text-center text-[13px] text-slate-700">{r.starter}</div>
                  <div className="px-4 py-4 text-center text-[13px] text-slate-900 font-semibold bg-lime-50/30">{r.growth}</div>
                  <div className="px-4 py-4 text-center text-[13px] text-slate-700">{r.pro}</div>
                  <div className="px-4 py-4 text-center text-[13px] text-slate-700">{r.enterprise}</div>
                  <div className="px-4 py-4 text-[12px] text-slate-500 leading-snug">{r.note}</div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-center text-[13px] text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Shipping, trade-credit insurance and financing (Net-30 / 60 / 90) pricing is built into how we deliver each service — no surprise line items.
              <br />
              <span className="text-slate-900 font-semibold">Lower transaction fees alone often pay for your Pro or Scale upgrade many times over.</span>
            </p>
          </div>
        </Reveal>

        {/* Enterprise band — moved to after roadmap section */}
      </section>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 7.5 — COMPARISON TABLE                                     */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 bg-slate-50/40 border-y border-slate-100" data-testid="section-compare">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>The honest comparison</Eyebrow>
              <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight leading-[1.02] text-slate-900">
                verqo vs <span className="text-slate-400">everything you've tried.</span>
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-slate-600 max-w-2xl">
                The other three options have been around for decades. Here's why none of them actually solve the working-capital problem in wholesale.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 overflow-x-auto -mx-6 md:mx-0 px-6 md:px-0" data-testid="compare-table-wrap">
              <div className="min-w-[860px] rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-[0_24px_64px_-32px_rgba(15,23,42,0.18)]">
                {/* Header row */}
                <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] border-b border-slate-200">
                  <div className="px-5 py-5 text-[10.5px] uppercase tracking-[0.22em] text-slate-400 bg-slate-50/60">Criteria</div>
                  <div className="px-4 py-5 text-center relative" style={{ background: "var(--brand-fg)" }}>
                    <div className="inline-flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded grid place-items-center" style={{ background: "var(--brand-accent)" }}>
                        <CheckVGlyph size={14} color="var(--brand-fg)" stroke={2.6} />
                      </div>
                      <span className="text-white font-semibold text-[13px]">verqo</span>
                    </div>
                    <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.18em] font-bold" style={{ background: "var(--brand-accent)", color: "var(--brand-fg)" }}>
                      Recommended
                    </div>
                  </div>
                  <div className="px-4 py-5 text-center text-[12px] font-semibold text-slate-600 bg-slate-50/60">Traditional<br />factoring</div>
                  <div className="px-4 py-5 text-center text-[12px] font-semibold text-slate-600 bg-slate-50/60">Net-60<br />terms</div>
                  <div className="px-4 py-5 text-center text-[12px] font-semibold text-slate-600 bg-slate-50/60">DIY<br />collections</div>
                </div>

                {COMPARE_ROWS.map((row, i) => (
                  <div
                    key={row.criterion}
                    className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] ${i < COMPARE_ROWS.length - 1 ? "border-b border-slate-100" : ""}`}
                    data-testid={`compare-row-${i}`}
                  >
                    <div className="px-5 py-5 text-[13px] font-semibold text-slate-900 bg-slate-50/40">{row.criterion}</div>
                    <div className="px-4 py-5 bg-lime-50/40 border-x border-lime-100">
                      <div className="flex items-start gap-1.5 text-[12.5px] text-slate-900 font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5 text-lime-600 mt-0.5 flex-shrink-0" />
                        <span>{row.verqo}</span>
                      </div>
                    </div>
                    <div className="px-4 py-5 text-[12.5px] text-slate-500 leading-snug">{row.factoring}</div>
                    <div className="px-4 py-5 text-[12.5px] text-slate-500 leading-snug">{row.net60}</div>
                    <div className="px-4 py-5 text-[12.5px] text-slate-500 leading-snug">{row.diy}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 text-center text-[12px] text-slate-500 max-w-2xl mx-auto">
              Honest comparison · sourced from public pricing pages and standard industry data. No vendor bashing.
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 7.5 — WHAT'S NEXT ON VERQO · seller roadmap + Enterprise   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-coming-soon">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>What's next on verqo</Eyebrow>
            <h2 className="mt-5 text-3xl md:text-5xl leading-[1.02] tracking-tight font-semibold text-slate-900">
              Shipping next, <span className="italic text-slate-400">for sellers.</span>
            </h2>
            <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-2xl mx-auto">
              The capabilities we're rolling out over the coming quarters. Buyer-side list is on the <a href="/buyers" className="underline decoration-lime-400 underline-offset-4 hover:text-slate-900" data-testid="roadmap-buyers-link">buyers page</a>.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 max-w-3xl mx-auto" data-testid="roadmap-columns">
          {/* SELLERS ROADMAP */}
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 md:p-9" data-testid="roadmap-sellers">
              <div className="flex items-center gap-2 mb-6">
                <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold inline-flex items-center gap-2">
                  <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
                  For sellers
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {SELLER_ROADMAP.map((r, i) => (
                  <li key={r.title} className="flex items-start gap-3" data-testid={`roadmap-seller-${i + 1}`}>
                    <span className="mono text-[10px] text-slate-400 tracking-[0.15em] mt-1 flex-shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="text-[14px] font-semibold text-slate-900 leading-snug">{r.title}</div>
                      <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{r.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* FAQ moved to the dedicated /faq route — accessible from the         */}
      {/* footer to keep this landing page focused on the conversion story.   */}
      {/* ═════════════════════════════════════════════════════════════════ */}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 8 — FINAL CTA                                              */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section id="cta" className="relative py-24 md:py-32 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-cta">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>The wholesale operating system</Eyebrow>
            <h2 className="mt-5 text-[36px] md:text-[60px] leading-[0.98] font-semibold tracking-tight text-slate-900">
              Six players. <span className="text-slate-400">One ledger.</span><br />
              Wholesale, <span data-serif className="bg-lime-300 px-3 -rotate-1 inline-block text-[1.05em]">instant.</span>
            </h2>
            <p className="mt-7 text-[15px] md:text-[17px] leading-relaxed text-slate-600 max-w-xl mx-auto">
              <strong className="text-slate-900">500+ wholesale buyers are already on verqo.</strong> Your suppliers can be the next to inherit that demand.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <a href="/waitlist?as=seller" data-testid="cta-final-primary" className="group inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all" style={{ background: "var(--brand-fg)" }}>
                Get paid today
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href={hidePricing ? "/pricing" : "#pricing"} data-testid="cta-final-secondary" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
                See pricing
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SECTION 8.5 — TRUST / SECURITY BADGES                              */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-20 px-6 md:px-12 bg-slate-50/40 border-t border-slate-100" data-testid="section-trust">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <div className="text-center text-[11px] uppercase tracking-[0.28em] text-slate-500 mb-10">
              Bank-grade compliance · examiner-ready out of the box
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="trust-grid">
              {TRUST_BADGES.map((b) => (
                <div
                  key={b.label}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-5 flex flex-col items-center text-center hover:border-lime-400 hover:-translate-y-0.5 transition-all"
                  data-testid={`trust-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="w-9 h-9 rounded-lg grid place-items-center mb-2.5" style={{ background: "var(--brand-fg)" }}>
                    <b.icon className="w-4 h-4" style={{ color: "var(--brand-accent)" }} strokeWidth={1.8} />
                  </div>
                  <div className="text-[12.5px] font-semibold text-slate-900">{b.label}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">{b.sub}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {!(hideFooter || trimmedForPreview) && (
      <footer className="px-6 md:px-12 max-w-[1280px] mx-auto py-12 border-t border-slate-200" data-testid="footer">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-10">
          <div className="col-span-2">
            <VerqoLockup size="sm" />
            <p className="mt-3 text-[11.5px] text-slate-500 leading-relaxed max-w-xs" data-testid="footer-tagline">
              Wholesale, instant. One platform that turns every Net-30/60/90 invoice into same-day cash — built by wholesalers, for wholesalers.
            </p>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-3">Product</div>
            <ul className="space-y-2 text-[12.5px] text-slate-700">
              <li><a href="#pricing" className="hover:text-slate-900 transition-colors" data-testid="footer-link-pricing">Pricing</a></li>
              <li><a href="#how" className="hover:text-slate-900 transition-colors" data-testid="footer-link-how">How it works</a></li>
              <li><Link to="/buyers" className="hover:text-slate-900 transition-colors" data-testid="footer-link-buyers">For buyers</Link></li>
              <li><Link to="/partners" className="hover:text-slate-900 transition-colors" data-testid="footer-link-partners">Partners</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-3">Company</div>
            <ul className="space-y-2 text-[12.5px] text-slate-700">
              <li><Link to="/about" className="hover:text-slate-900 transition-colors" data-testid="footer-link-about">About us</Link></li>
              <li><Link to="/faq" className="hover:text-slate-900 transition-colors" data-testid="footer-link-faq">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-slate-900 transition-colors" data-testid="footer-link-contact">Contact us</Link></li>
              <li><a href="/legal/terms" className="hover:text-slate-900 transition-colors" data-testid="footer-link-terms">Terms &amp; Privacy</a></li>
            </ul>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-slate-100 text-[10.5px] uppercase tracking-[0.22em] text-slate-400">
          <div>© 2026 verqo · Wholesale, instant.</div>
          <div className="normal-case tracking-normal text-[11px] text-slate-500">All rights reserved.</div>
        </div>
      </footer>
      )}
    </div>
  );
}
